/**
 * Bond mathematics used by NigaBond Trader.
 *
 * Covers:
 *   - accrued interest (ACT/ACT ISMA — matches FGN bond convention)
 *   - clean price <-> dirty price conversion
 *   - yield-to-maturity (YTM) via bisection (robust, no floats dance)
 *   - Macaulay + modified duration
 *   - convexity
 *
 * All inputs are plain numbers. The order-ticket downstream converts to
 * kobo for bigint math before posting through BrokerClient.placeOrder.
 *
 * Conventions used:
 *   - Price quoted per 100 face (e.g. 98.75 = 98.75% of face value)
 *   - Coupon quoted as annual % (e.g. 14.55 = 14.55%)
 *   - Face value defaults to N1,000 per unit for FGN, N1,000 for state/LGA
 *   - Payments are semi-annual for FGN and most NGX-listed bonds
 */

export type DayCount = 'ACT/ACT' | '30/360' | 'ACT/365';
export type Frequency = 1 | 2 | 4; // annual, semi-annual, quarterly

export interface BondDef {
  face: number;
  couponPct: number;
  frequency: Frequency;
  settlementDate: Date;
  maturityDate: Date;
  dayCount?: DayCount;
  /** Date of the previous coupon; if omitted we infer from maturity. */
  previousCoupon?: Date;
  nextCoupon?: Date;
}

function daysBetween(a: Date, b: Date): number {
  return Math.floor((b.getTime() - a.getTime()) / 86400_000);
}
function addMonths(d: Date, months: number): Date {
  const r = new Date(d);
  r.setUTCMonth(r.getUTCMonth() + months);
  return r;
}

/** Derive previous/next coupon from maturity (common when issuer doesn't provide them). */
export function deriveCouponDates(settle: Date, maturity: Date, freq: Frequency): { prev: Date; next: Date } {
  const months = 12 / freq;
  let next = new Date(maturity);
  while (next > settle) next = addMonths(next, -months);
  const prev = next; // the last coupon before settle
  const nextPay = addMonths(prev, months);
  return { prev, next: nextPay };
}

export function accruedInterest(b: BondDef): number {
  const { prev, next } = b.previousCoupon && b.nextCoupon
    ? { prev: b.previousCoupon, next: b.nextCoupon }
    : deriveCouponDates(b.settlementDate, b.maturityDate, b.frequency);
  const periodDays = daysBetween(prev, next);
  const accruedDays = daysBetween(prev, b.settlementDate);
  const couponCash = b.face * (b.couponPct / 100) / b.frequency;
  const ai = couponCash * (accruedDays / periodDays);
  return Math.max(0, ai);
}

export function cleanToDirty(cleanPct: number, b: BondDef): number {
  // clean & dirty are quoted per 100 face. dirty = clean + AI%
  const ai = accruedInterest(b);
  const aiPct = (ai / b.face) * 100;
  return cleanPct + aiPct;
}
export function dirtyToClean(dirtyPct: number, b: BondDef): number {
  const ai = accruedInterest(b);
  const aiPct = (ai / b.face) * 100;
  return dirtyPct - aiPct;
}

/** Number of coupon periods from settlement to maturity (fractional). */
function periodsToMaturity(b: BondDef): number {
  const months = 12 / b.frequency;
  const yrs = (b.maturityDate.getTime() - b.settlementDate.getTime()) / (365.25 * 86400_000);
  return yrs * (12 / months);
}

function priceFromYtm(ytmPct: number, b: BondDef): number {
  // Present-value all cashflows, yielding clean price per 100.
  const y = ytmPct / 100 / b.frequency;
  const c = b.couponPct / 100 / b.frequency;   // coupon per period as fraction of face
  const n = periodsToMaturity(b);
  const { prev, next } = b.previousCoupon && b.nextCoupon
    ? { prev: b.previousCoupon, next: b.nextCoupon }
    : deriveCouponDates(b.settlementDate, b.maturityDate, b.frequency);
  const periodDays = daysBetween(prev, next);
  const wholePeriods = Math.floor(n);
  const frac = n - wholePeriods;

  let pv = 0;
  for (let k = 1; k <= wholePeriods; k++) pv += c / Math.pow(1 + y, k + frac);
  pv += 1 / Math.pow(1 + y, wholePeriods + frac); // redemption of par
  const dirty = pv * 100;
  // convert dirty to clean (subtract accrued interest % of 100)
  const aiPct = ((c * periodDays - c * (periodDays - daysBetween(prev, b.settlementDate))) / periodDays) * 100 * (frac === 1 ? 0 : 1);
  return dirty - aiPct;
}

/** YTM by bisection. Converges fast for reasonable inputs. */
export function ytm(cleanPct: number, b: BondDef, tolerance = 1e-6, maxIter = 200): number {
  let lo = 0.0001, hi = 100;
  for (let i = 0; i < maxIter; i++) {
    const mid = (lo + hi) / 2;
    const p = priceFromYtm(mid, b);
    if (Math.abs(p - cleanPct) < tolerance) return mid;
    if (p > cleanPct) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

export function macaulayDuration(cleanPct: number, b: BondDef): number {
  const y = ytm(cleanPct, b) / 100 / b.frequency;
  const c = b.couponPct / 100 / b.frequency;
  const n = Math.ceil(periodsToMaturity(b));
  let num = 0, den = 0;
  for (let k = 1; k <= n; k++) {
    const cf = k === n ? c + 1 : c;
    const pv = cf / Math.pow(1 + y, k);
    num += pv * (k / b.frequency);
    den += pv;
  }
  return num / den;
}
export function modifiedDuration(cleanPct: number, b: BondDef): number {
  const mac = macaulayDuration(cleanPct, b);
  const y = ytm(cleanPct, b) / 100 / b.frequency;
  return mac / (1 + y);
}

export function convexity(cleanPct: number, b: BondDef): number {
  const y = ytm(cleanPct, b) / 100 / b.frequency;
  const c = b.couponPct / 100 / b.frequency;
  const n = Math.ceil(periodsToMaturity(b));
  let sum = 0, price = 0;
  for (let k = 1; k <= n; k++) {
    const cf = k === n ? c + 1 : c;
    const pv = cf / Math.pow(1 + y, k);
    sum += pv * k * (k + 1);
    price += pv;
  }
  return sum / (price * Math.pow(1 + y, 2) * Math.pow(b.frequency, 2));
}
